/* global PubSub, Cart */

// Advent calendar Early Bird presale.
// Keeps the calendar in its own order while the phase runs: the cart drawer
// re-renders server-side (Cart.refresh), so the warning and the blocked checkout
// come from Liquid. This module handles the two "keep" choices and re-checks the
// cart before checkout. There is no end date: the phase runs until the
// `early-bird` tag is removed from the calendar product.
const CalendarPresale = (function CalendarPresaleModule() {
   const FOCUS_DELAY_MS = 250;

   let config = null;
   let variantIds = new Set();
   let pending = Promise.resolve();
   let hadConflict = false;

   function active() {
      if (!config?.enabled) return false;
      return !config.startsAt || Date.now() >= Date.parse(config.startsAt);
   }

   function isCalendarItem(item) {
      return item.handle === config.handle || variantIds.has(String(item.variant_id));
   }

   function hasCalendar(cart) {
      return active() && cart.items.some(isCalendarItem);
   }

   function hasConflict(cart) {
      return hasCalendar(cart) && cart.items.some((item) => !isCalendarItem(item));
   }

   async function readCart() {
      const response = await fetch(`${config.cartUrl}.js`, { cache: "no-store" });
      if (!response.ok) throw new Error(config.errorMessage);
      return response.json();
   }

   // Serialize cart writes, across tabs where Web Locks is available.
   function enqueue(task) {
      const run = () =>
         navigator.locks ? navigator.locks.request("kruemel-calendar-cart", task) : task();
      const result = pending.then(run);
      pending = result.catch(() => {});
      return result;
   }

   function refresh() {
      return new Promise((resolve) => {
         Cart.refresh(resolve);
      });
   }

   function showCartError() {
      const summary = document.querySelector("[data-cart-summary]");
      if (!summary || summary.querySelector("[data-calendar-error]")) return;
      const error = document.createElement("p");
      error.className = "text-base text-destructive";
      error.setAttribute("data-calendar-error", "");
      error.setAttribute("role", "alert");
      error.textContent = config.errorMessage;
      summary.prepend(error);
   }

   function focusWarning() {
      const warning = document.querySelector("[data-calendar-cart-warning]");
      if (!warning) return;
      warning.focus({ preventScroll: true });
      warning.scrollIntoView({ block: "nearest", behavior: "smooth" });
   }

   // Drop every line on the unwanted side, keep quantities and properties of the rest.
   function keepItems(kind) {
      return enqueue(async () => {
         const cart = await readCart();
         const updates = {};
         cart.items.forEach((item) => {
            const keep = kind === "calendar" ? isCalendarItem(item) : !isCalendarItem(item);
            if (!keep) updates[item.key] = 0;
         });
         if (Object.keys(updates).length) {
            const response = await fetch(`${config.cartUrl}/update.js`, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ updates }),
            });
            if (!response.ok) throw new Error(config.errorMessage);
         }
         await refresh();
      });
   }

   async function handleKeepClick(button) {
      const warning = button.closest("[data-calendar-cart-warning]");
      if (!warning || warning.getAttribute("aria-busy") === "true") return;
      warning.setAttribute("aria-busy", "true");
      const buttons = [...warning.querySelectorAll("button")];
      buttons.forEach((el) => Object.assign(el, { disabled: true }));
      try {
         await keepItems(button.dataset.calendarKeep);
      } catch {
         showCartError();
         buttons.forEach((el) => Object.assign(el, { disabled: false }));
         warning.removeAttribute("aria-busy");
      }
   }

   // Re-check against a fresh cart before leaving for checkout: the drawer may be
   // stale if another tab changed the cart. A failed read must not lock the
   // customer out, the server-rendered summary already blocks real conflicts.
   async function handleCheckoutClick(link) {
      if (link.getAttribute("aria-disabled") === "true") return;
      try {
         const cart = await readCart();
         if (hasConflict(cart)) {
            await refresh();
            PubSub.publish("cart:open");
            return;
         }
      } catch {
         // fall through to checkout
      }
      window.location.assign(link.href);
   }

   function handleCartUpdated() {
      if (!active()) return;
      const conflict = Boolean(document.querySelector("[data-calendar-cart-warning]"));
      // CartDrawer.open moves focus to its close button after 100ms, so wait it out.
      if (conflict && !hadConflict) setTimeout(focusWarning, FOCUS_DELAY_MS);
      hadConflict = conflict;
   }

   function bindEvents() {
      document.addEventListener("click", (e) => {
         if (!active()) return;

         const keepButton = e.target.closest("[data-calendar-keep]");
         if (keepButton) {
            e.preventDefault();
            handleKeepClick(keepButton);
            return;
         }

      });

      PubSub.subscribe("cart:updated", handleCartUpdated);
   }

   function init() {
      const configEl = document.querySelector("[data-calendar-presale-config]");
      if (!configEl) return;
      try {
         config = JSON.parse(configEl.textContent);
      } catch {
         return;
      }
      variantIds = new Set((config.variantIds ?? []).map(String));
      hadConflict = Boolean(document.querySelector("[data-calendar-cart-warning]"));

      if (active()) bindEvents();
   }

   return {
      init,
      active,
      hasCalendar,
      hasConflict,
   };
})();

document.addEventListener("DOMContentLoaded", CalendarPresale.init);
